import { useSelector, useDispatch } from "react-redux";

import SoftButton from "components/SoftButton";
import SoftBox from "components/SoftBox";

import { useEffect, useState, useMemo } from "react";
import { Box, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";

import { useTranslation } from "react-i18next";
import { storeOneJson } from "api/FlowDesignerApis";
import { setFlowConfiguration, setSavedFlow } from "redux/actions/flowDesigner/flowsActionCreator";
import { clearToolboxSelectedMenuItem, closeToolboxDetails } from "redux/actions/flowDesigner/settingsActionCreator";
import { openDiscardDialog } from "redux/actions/flowDesigner/commonActionCreator";
import DiscardChangesDialog from "layouts/Flow Designer/components/DiscardChangesDialog";
import { updateFlow } from "api/FlowDesignerApis";
import { toast } from "react-toastify";
import { MAX_NUM_FLOW_CONFIG_PARAMETERS, MAX_FLOW_CONFIG_SIZE, MAX_FLOW_CONFIG_KEY_SIZE } from "constants/flowDesigner";
import ConfigurationFields from "../components/ConfigurationFields";
import {
   cloneConfiguration,
   configurationToFields,
   createConfigurationField,
   fieldsToConfiguration,
   getFlowSettings,
   getTransmittableFlow,
   isConfigurationKeyMissing,
   isConfigurationKeyOverLimit,
   isConfigurationNumberInvalid,
   isConfigurationObjectInvalid,
   isConfigurationTypeMissing,
   isConfigurationValueMissing,
   isConfigurationValueOverLimit
} from "../helpers";
import { cacheUpdateSocketIoClient } from "App";

const ButtonsWrapper = styled(Box)(({ theme }) => ({ display: "flex", justifyContent: "flex-end", marginTop: theme.spacing(2), position: "relative", bottom: 0, width: "100%" }));

const StyledStack = styled(Stack)(({ theme }) => ({
   width: "100%",
   flex: 1,
   minHeight: 0,
   overflowY: "auto",
   overflowX: "hidden",
   "&::-webkit-scrollbar": { width: "8px", background: "transparent", transition: "opacity 0.2s", opacity: 0 },
   "&:hover::-webkit-scrollbar": { opacity: 1 },
   "&::-webkit-scrollbar-thumb": { background: theme.palette.grey[400], borderRadius: "8px" },
   "&::-webkit-scrollbar-track": { background: "transparent" }
}));

const getLimitValidationError = (fields) => {
   if (fields.some(isConfigurationKeyOverLimit)) return `Flow configuration keys must be ${MAX_FLOW_CONFIG_KEY_SIZE} characters or less.`;
   if (fields.some(isConfigurationValueOverLimit)) return `Flow configuration string, object, and array values must be ${MAX_FLOW_CONFIG_SIZE} characters or less.`;

   return "";
};

const Private = ({ currentSavedFlow, allowEditWorkflow }) => {
   const { flowConfiguration } = useSelector((store) => store.designer.flows);
   const { t } = useTranslation("flowDesigner");
   const [submitting, setSubmitting] = useState(false);
   const [initialFields, setInitialFields] = useState(() => configurationToFields(flowConfiguration));
   const [fields, setFields] = useState(() => configurationToFields(flowConfiguration));
   const [touchedFields, setTouchedFields] = useState({});
   const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
   const dispatch = useDispatch();

   const isChanged = useMemo(() => {
      return JSON.stringify(fields) !== JSON.stringify(initialFields);
   }, [fields, initialFields]);

   const limitValidationError = useMemo(() => getLimitValidationError(fields), [fields]);
   const hasInvalidFields = useMemo(() => fields.some((field) => isConfigurationKeyMissing(field) || isConfigurationTypeMissing(field) || isConfigurationValueMissing(field) || isConfigurationNumberInvalid(field) || isConfigurationObjectInvalid(field)), [fields]);
   const disableAddField = fields.length >= MAX_NUM_FLOW_CONFIG_PARAMETERS || !allowEditWorkflow;

   const addField = () => {
      if (disableAddField) return;

      setHasUnsavedChanges(true);

      setFields((prev) => {
         if (prev.length >= MAX_NUM_FLOW_CONFIG_PARAMETERS) return prev;

         return [...prev, createConfigurationField()];
      });
   };

   const removeField = (index) => {
      setHasUnsavedChanges(true);
      setFields((prev) => prev.filter((_, i) => i !== index));
   };

   const updateField = (index, prop, value) => {
      setHasUnsavedChanges(true);

      if (prop === "type") {
         setTouchedFields((current) => {
            const { [`${index}-value`]: _, ...rest } = current;
            return rest;
         });
      }

      setFields((prev) => {
         const copy = [...prev];
         copy[index][prop] = value;

          if (prop === "type") {
             switch (value) {
                case "boolean":
                   copy[index].value = true;
                   break;
                case "number":
                   copy[index].value = "";
                   break;
                case "string":
                   copy[index].value = "";
                   break;
                case "object":
                   copy[index].value = "{}";
                   break;
             }
         }

         return copy;
      });
   };

   const markFieldTouched = (index, field) => {
      setTouchedFields((current) => ({ ...current, [`${index}-${field}`]: true }));
   };

   const handleSubmit = async () => {
      if (hasInvalidFields) {
         toast.error("Every flow configuration field needs a key, value, and type.");
         return;
      }

      if (limitValidationError) {
         toast.error(limitValidationError);
         return;
      }

      setSubmitting(true);
      const builtObject = fieldsToConfiguration(fields);
      const storeJsonKey = `${currentSavedFlow.tenant_id}-${currentSavedFlow.id}-${currentSavedFlow.current_version}-config`;
      const storeJsonResult = await storeOneJson(storeJsonKey, "", builtObject, false, 0);
      
      if (storeJsonResult.isSuccessful) {
         const currentFlowSettings = getFlowSettings(currentSavedFlow);
         
         const hadFlowConfiguration = currentFlowSettings?.hasFlowConfiguration,
            hasFlowConfiguration = Object.keys(builtObject).length > 0,
            updatedFlowSettings = {
               ...currentFlowSettings,
               hasFlowConfiguration
            };
            
         if (hadFlowConfiguration !== hasFlowConfiguration) {
            const flowUpdated = await updateFlow(
               currentSavedFlow.tenant_id,
               currentSavedFlow.id,
               getTransmittableFlow(currentSavedFlow, updatedFlowSettings),
               false
            );

            if (!flowUpdated) {
               toast.error(`Unable to save modified flow - ensure that an instance of the cxf-executor is running and that the Flow Designer is connected to it. Then, reload and try again.`, {
                  autoClose: 5000,
                  pauseOnHover: true
               });
               setSubmitting(false);
               return;
            }
         }

         if (cacheUpdateSocketIoClient?.connected) {
            cacheUpdateSocketIoClient.emit("flowConfigurationUpdate", { tenantId: currentSavedFlow.tenant_id, flowId: currentSavedFlow.id, flowName: currentSavedFlow.name });
            console.debug(`Requested flow configuration cache update in cxf-executor for flow ${currentSavedFlow.id} (${currentSavedFlow.name}) for tenant ${currentSavedFlow.tenant_id}`);
         } else {
            toast.error('Unable to request flow configuration cache update in cxf-executor because the service is not running or not reachable ');
            return;
         }

         dispatch(setSavedFlow({ flow_settings: JSON.stringify(updatedFlowSettings) }));
         toast.success("Flow configuration saved successfully");
         dispatch(setFlowConfiguration(builtObject));
         setInitialFields(cloneConfiguration(fields));
         setHasUnsavedChanges(false);
      } else toast.error(`Failed to save flow configuration: ${storeJsonResult.message}`);

       setSubmitting(false);
   };

   const handleCancel = () => {
      setFields(cloneConfiguration(initialFields));
      setHasUnsavedChanges(false);
      dispatch(closeToolboxDetails());
      dispatch(clearToolboxSelectedMenuItem());
   };

   const handleClose = () => {
      if (isChanged) {
         dispatch(openDiscardDialog());
         return;
      }

      handleCancel();
   };

   useEffect(() => {
      const parsedFields = configurationToFields(flowConfiguration);

      if (hasUnsavedChanges) {
         if (initialFields.length === 0) {
            setInitialFields(cloneConfiguration(parsedFields));
            setFields((current) => [...parsedFields, ...current]);
         }
         return;
      }

      setInitialFields(cloneConfiguration(parsedFields));
      setFields(parsedFields);
   }, [flowConfiguration, hasUnsavedChanges, initialFields.length]);

   return (
      <Box sx={{ mt: 2, display: "flex", flex: 1, flexDirection: "column", minHeight: 0 }}>
         <StyledStack spacing={2} sx={{ pr: 2 }}>
            <ConfigurationFields
               fields={fields}
               onChange={updateField}
               onRemove={removeField}
               onBlur={markFieldTouched}
               protectValueSize
               getFieldState={(field, index) => {
                  const keyOverLimit = isConfigurationKeyOverLimit(field);
                  const valueOverLimit = isConfigurationValueOverLimit(field);
                  const keyError = touchedFields[`${index}-key`] && (isConfigurationKeyMissing(field) || keyOverLimit);
                  const valueError = touchedFields[`${index}-value`] && (isConfigurationValueMissing(field) || isConfigurationNumberInvalid(field) || isConfigurationObjectInvalid(field) || valueOverLimit);

                  return {
                     keyError,
                     keyHelperText: keyError ? (isConfigurationKeyMissing(field) ? "Key is required." : `Maximum ${MAX_FLOW_CONFIG_KEY_SIZE} characters`) : "",
                     typeError: touchedFields[`${index}-type`] && isConfigurationTypeMissing(field),
                     valueError,
                     valueHelperText: valueError ? (isConfigurationValueMissing(field) ? "Value is required." : isConfigurationNumberInvalid(field) ? "Value must be a valid number" : isConfigurationObjectInvalid(field) ? "Value must be a valid JSON object." : `Maximum ${MAX_FLOW_CONFIG_SIZE} characters`) : ""
                  };
               }}
            />
            <SoftBox sx={{ width: "100%", display: "flex", justifyContent: "center", py: 1 }}>
               <SoftButton sx={{ minWidth: "180px" }} variant="outlined" color="info" onClick={addField} disabled={disableAddField}>
                  Add Field
               </SoftButton>
            </SoftBox>
         </StyledStack>
         <ButtonsWrapper sx={{ flexShrink: 0 }}>
            <SoftBox className="btns-action" sx={{ display: "flex", width: "100%", gap: 2, mt: 2, padding: "5px", justifyContent: "flex-end" }}>
               <SoftButton sx={{ minWidth: "110px", m: 0 }} variant="outlined" color="info" onClick={handleClose}>
                  {t("buttons.close")}
               </SoftButton>
                <SoftButton sx={{ minWidth: "110px", m: 0 }} color="primary" variant="contained" disabled={submitting || !allowEditWorkflow || !isChanged || hasInvalidFields || Boolean(limitValidationError)} onClick={handleSubmit}>
                  {t("buttons.save")}
               </SoftButton>
            </SoftBox>
         </ButtonsWrapper>
         <DiscardChangesDialog onAccept={handleCancel} />
      </Box>
   );
};

export default Private;
